import dayjs from "dayjs";
import { formatBytes } from "../../../../../../../../utilities/helpers";
import { DateFormats } from "../../../../../../../../utilities/dates";
import SourceFilesListContextMenu from "./SourceFilesListContextMenu";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import useMediaInfo from "../../../../../../../../hooks/useMediaInfo";
import { MediaInfoResult, Track } from "mediainfo.js";
import useAsyncCallback from "../../../../../../../../hooks/useAsyncCallback";
import useHandleContextMenu from "./useHandleContextMenu";
import { SourceFilesListProps } from "./SourceFilesList";
import { Reorder, useDragControls } from "framer-motion";
import { parseDatesFromFilename } from "../../../../../../../../utilities/parseDatesFromFilename/parseDatesFromFilename";
import { ExtraSourceFileDataStart, ExtraSourceFileDataStartSource, SourceFile } from "../../../../ImportWizard";
import { OverlayTrigger, Popover } from "react-bootstrap";

interface SourceFilesListItemProps extends SourceFilesListProps {
    sourceFile: SourceFile;
    overrideStart?: dayjs.Dayjs;
    updateSourceFile: (_: SourceFile) => void;
    index: number;
    handleDragEnd: (index: number) => void;
    handleClick: (event: React.MouseEvent, index: number) => void;
    handleRemoveFile: (event: React.MouseEvent, index: number) => void;
}

export function millisecondsToTimeString(ms: number | undefined): string | undefined {
    if (ms === undefined) {
        return undefined;
    }

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600) + "h";
    const minutes = Math.floor((totalSeconds % 3600) / 60) + "m";
    const seconds = (totalSeconds % 60) + "s";

    return [hours, minutes, seconds]
        .map((v) => v.toString().padStart(2, "0"))
        .filter((v, i) => v !== "0h" || i > 0) // Skip leading hours if zero
        .join(" ");
}

function SourceFilesListItem(props: SourceFilesListItemProps) {
    const name = props.sourceFile.file.name;
    const size = formatBytes(props.sourceFile.file.size);

    const { isShowingMenu, hideMenu, menuCoords, removeSelectedFiles, handleContextMenu } = useHandleContextMenu(
        props,
        props.handleClick,
    );

    // One MediaInfo instance is required per file, otherwise the same data will be returned for all
    // of them using `analyzeFile`
    // TODO: investigate whether there is a way to use just a single MediaInfo instance to analyze all files
    const { mediaInfo, analyzeFile } = useMediaInfo();
    const [fileData, setFileData] = useState<Track[] | undefined>(undefined);
    useAsyncCallback(async () => {
        if (!mediaInfo || !props.sourceFile) {
            return;
        }

        await analyzeFile(props.sourceFile.file).then((res) =>
            setFileData((res as MediaInfoResult)?.media?.track ?? []),
        );
    }, [props.sourceFile.file.name, mediaInfo]);

    const errorMsg = props.errorMsgs[props.index];
    const isInvalid = errorMsg !== undefined;
    const isSelected = props.selectedFiles.includes(props.index);

    const videoTrack = fileData?.find((t) => t["@type"] == "Video");
    const durationMs = videoTrack?.["Duration"]
        ? parseFloat(videoTrack?.["Duration"].toString() ?? "0") * 1000
        : undefined;
    const duration = durationMs ? millisecondsToTimeString(durationMs) : undefined;
    const codec = videoTrack?.CodecID;
    const width = videoTrack?.["Width"];
    const height = videoTrack?.["Height"];
    const fps = Math.round(parseFloat(videoTrack?.["FrameRate"]?.toString() ?? "0"));

    const audioTrack = fileData?.find((t) => t["@type"] == "Audio");

    const start: ExtraSourceFileDataStart = useMemo(() => {
        if (props.overrideStart) {
            return { value: props.overrideStart, source: "override" };
        }

        let { start: startValue } = parseDatesFromFilename(name);
        let startSource: ExtraSourceFileDataStartSource = "filename";

        // Fallback to the encoded date parsed by MediaInfo
        if ((!startValue || !startValue.isValid()) && videoTrack?.["Encoded_Date"]) {
            startValue = dayjs(videoTrack?.["Encoded_Date"]);
            startSource = "media";
        }

        // Fallback to file's last modified time
        if (!startValue || !startValue.isValid()) {
            startValue = dayjs(props.sourceFile.file.lastModified);
            startSource = "filemodified";
        }

        return { value: startValue, source: startSource };
    }, [name, videoTrack?.["Encoded_Date"], props.overrideStart]);

    useEffect(() => {
        if (fileData !== undefined) {
            props.updateSourceFile({
                file: props.sourceFile.file,
                duration: durationMs,
                hasAudioTrack: audioTrack !== undefined,
                start,
                width,
                height,
                isLoadingFileData: false,
            });
        }
    }, [fileData === undefined]);

    const dragControls = useDragControls();

    // Used to hide the popover while dragging
    // TODO: reset position of popover after drag is finished, as currently it will re-appear in its
    // previous position
    const [forceHidePopover, setForceHidePopover] = useState(false);

    return (
        // NOTE: rendering the entire overlay and popover even for valid files is required to prevent
        // the re-order group from believing that a change has happened to the source of the drag event.
        <OverlayTrigger
            placement="bottom"
            delay={{ show: 150, hide: 250 }}
            overlay={
                <Popover>
                    {!forceHidePopover && isInvalid && <Popover.Body className="text-danger">{errorMsg}</Popover.Body>}
                </Popover>
            }
        >
            <Reorder.Item
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                value={props.sourceFile}
                dragControls={dragControls}
                dragListener={false}
                onDragEnd={() => {
                    props.handleDragEnd(props.index);
                    setForceHidePopover(false);
                }}
                onDragStart={() => {
                    setForceHidePopover(true);
                }}
                onPointerDown={(e) => {
                    // Ignore right/middle clicks and other gestures on touch devices
                    if (e.button !== 0) {
                        return;
                    }
                    dragControls.start(e);
                    props.handleClick(e, props.index);
                }}
                onContextMenu={(e) => {
                    handleContextMenu(e, props.index);
                }}
                onMouseLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    hideMenu();
                }}
                className={`source-files-list-row${isSelected ? " selected" : ""}${isInvalid ? " invalid" : ""}`}
            >
                <span>{props.index + 1}</span>
                <span className="text-truncate" title={name}>
                    {name}
                </span>
                <span>{start.value.format(DateFormats.dayMonthYearWithTimeSeconds)}</span>
                <span>{duration}</span>
                <span className="text-truncate" title={codec}>
                    {codec}
                </span>
                <span>{isNaN(fps) ? undefined : fps}</span>
                <span>{size}</span>
                <button
                    title="Remove this source file from the list"
                    className="button-with-icon fs-6"
                    onClick={(e) => props.handleRemoveFile(e, props.index)}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>

                <SourceFilesListContextMenu
                    show={isShowingMenu}
                    coords={menuCoords}
                    removeSelectedFiles={removeSelectedFiles}
                />
            </Reorder.Item>
        </OverlayTrigger>
    );
}

export default SourceFilesListItem;
